import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { Button } from '../components/common/Button'
import {
  Heart,
  Shield,
  Phone,
  Users,
  CheckCircle,
  ArrowRight,
  Mail,
  Pill,
  MessageCircle,
  CircleDollarSign,
  Globe,
} from 'lucide-react'

/**
 * Landing page - Company Home page with brand styling
 * "Get Started" buttons navigate to /onboarding (mode selection)
 */
export function Landing() {
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [showOnboardingChoice, setShowOnboardingChoice] = useState(false)
  const [showPasswordPrompt, setShowPasswordPrompt] = useState(false)
  const [password, setPassword] = useState('')
  const [passwordError, setPasswordError] = useState(false)

  const handleGetStarted = () => {
    setShowPasswordPrompt(true)
    setPassword('')
    setPasswordError(false)
  }

  const handlePasswordSubmit = () => {
    if (password === 'littlesister2026') {
      setShowPasswordPrompt(false)
      setShowOnboardingChoice(true)
    } else {
      setPasswordError(true)
    }
  }

  const handleVoiceOnboarding = () => {
    navigate('/voice-onboarding')
  }

  const handleFormOnboarding = () => {
    navigate('/onboarding')
  }

  const handleEmailSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    // In real app, would send email to backend
    console.log('Email submitted:', email)
    // Then navigate to onboarding
    navigate('/onboarding')
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-sky-50 to-white">
      {/* Navigation Header */}
      <nav
        style={{
          backgroundColor: 'rgba(255, 255, 255, 0.9)',
          backdropFilter: 'blur(8px)',
          borderBottom: '1px solid #f3f4f6',
          position: 'sticky',
          top: 0,
          zIndex: 50,
        }}
      >
        <div
          style={{
            maxWidth: '1280px',
            margin: '0 auto',
            padding: '0 1rem',
          }}
        >
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              padding: '1rem 0',
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '0.75rem',
              }}
            >
              <img
                src="/favicon.png"
                alt="Little Sister Logo"
                style={{
                  height: '40px',
                  width: '40px',
                  objectFit: 'contain',
                }}
              />
              <span
                style={{
                  fontSize: '1.5rem',
                  fontWeight: 700,
                  color: '#111827',
                }}
              >
                Little Sister
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '1.5rem',
              }}
            >
              <div
                style={{
                  display: window.innerWidth >= 768 ? 'flex' : 'none',
                  alignItems: 'center',
                  gap: '1.5rem',
                }}
              >
                <a
                  href="#"
                  style={{
                    color: '#4b5563',
                    fontWeight: 500,
                    textDecoration: 'none',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.color = '#111827')}
                  onMouseLeave={e => (e.currentTarget.style.color = '#4b5563')}
                >
                  Home
                </a>
                <a
                  href="/dashboard"
                  style={{
                    color: '#4b5563',
                    fontWeight: 500,
                    textDecoration: 'none',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.color = '#111827')}
                  onMouseLeave={e => (e.currentTarget.style.color = '#4b5563')}
                >
                  Dashboard
                </a>
              </div>
              <Button onClick={handleGetStarted}>Get Started</Button>
            </div>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="relative overflow-hidden bg-gradient-to-r from-blue-50 via-sky-50 to-indigo-50">
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-20">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="space-y-8">
              <div className="space-y-4">
                <span className="inline-block bg-blue-100 text-blue-800 border border-blue-200 px-4 py-2 rounded-full text-sm font-medium">
                  Free During Beta Phase
                </span>
                <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 leading-tight">
                  Bridging the Digital Gap
                  <span className="block text-blue-600">— One Call at a Time</span>
                </h1>
                <p className="text-xl text-gray-600 leading-relaxed max-w-xl">
                  Little Sister is a voice-based AI companion that helps your loved one navigate the
                  digital world with confidence, while keeping families connected and informed —
                  anytime, on any phone.
                </p>
              </div>

              <div className="flex flex-col sm:flex-row gap-4">
                <Button onClick={handleGetStarted} size="large">
                  Get Early Access
                  <ArrowRight style={{ width: '20px', height: '20px', marginLeft: '8px' }} />
                </Button>
              </div>
            </div>

            <div className="relative">
              <div className="absolute inset-0 bg-gradient-to-r from-purple-100 to-blue-100 rounded-3xl transform rotate-3 opacity-20"></div>
              <img
                src="https://qtrypzzcjebvfcihiynt.supabase.co/storage/v1/object/public/base44-prod/public/79319d0e1_image.png"
                alt="Adult daughter helping elderly father with smartphone"
                className="relative rounded-3xl shadow-2xl w-full h-auto transform -rotate-1"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl font-bold text-gray-900">Designed for Peace of Mind</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Little Sister provides comprehensive support that keeps your loved ones safe,
              connected, and independent.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <FeatureCard
              icon={<Pill className="w-7 h-7 text-blue-600" />}
              iconBgColor="bg-blue-100"
              title="Medication Reminders"
              description="Gentle, personalized daily calls ensure medications are taken on time. Custom scheduling adapts to each person's routine and preferences."
              features={['Custom daily call scheduling', 'Voice-based confirmation system']}
            />

            <FeatureCard
              icon={<MessageCircle className="w-7 h-7 text-sky-600" />}
              iconBgColor="bg-sky-100"
              title="Daily Check-ins"
              description="Warm, conversational daily wellness checks that feel natural. Any missed responses trigger instant family notifications for immediate follow-up."
              features={['Natural conversation flow', 'Instant family notifications']}
            />

            <FeatureCard
              icon={<MessageCircle className="w-7 h-7 text-amber-600" />}
              iconBgColor="bg-amber-100"
              title="Voice-Based AI Assistant"
              description="Advanced AI integration provides helpful answers to everyday questions, from app instructions to basic tech support, all through simple voice conversations."
              features={['LLM-powered assistance', 'Available anytime by phone']}
            />
          </div>
        </div>
      </section>

      {/* Onboarding Section */}
      <section className="py-24 bg-gradient-to-r from-stone-50 to-amber-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl font-bold text-gray-900">
              Simple Setup, Lasting Peace of Mind
            </h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto">
              Getting started with Little Sister is designed to be effortless for both you and your
              loved one.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <OnboardingStep
              number="1"
              title="Family Dashboard Setup"
              description="Adult children use our dedicated family dashboard to configure medications, emergency contacts, and preferences together with their parent."
            />
            <OnboardingStep
              number="2"
              title="Dedicated Phone Number"
              description="Your aging parent receives a dedicated phone number and AI Little Sister agent for voice-based interactions - no apps or technology learning required."
            />
            <OnboardingStep
              number="3"
              title="Always Available Support"
              description="Little Sister calls your parent daily for check-ins and medication reminders, plus your parent can call anytime for assistance or conversation."
            />
          </div>
        </div>
      </section>

      {/* Why We Chose Voice Section */}
      <section className="py-24 bg-gray-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center space-y-4 mb-16">
            <h2 className="text-4xl font-bold text-gray-900">Why We Chose Voice</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Little Sister is designed to be simple, familiar, and accessible for everyone. That's
              why we built it to work through a regular phone call — no apps, no special devices, no
              new tech to learn.
            </p>
          </div>

          <div className="max-w-4xl mx-auto">
            <h3 className="text-2xl font-semibold text-center text-gray-800 mb-10">
              The Benefits of Voice
            </h3>
            <div className="grid md:grid-cols-3 gap-8 text-center">
              <VoiceBenefit
                icon={<Phone className="w-8 h-8 text-blue-600" />}
                iconBgColor="bg-blue-100"
                title="Familiar"
                description="If you can make a call, you can use Little Sister. It feels natural and effortless."
              />
              <VoiceBenefit
                icon={<CircleDollarSign className="w-8 h-8 text-sky-600" />}
                iconBgColor="bg-sky-100"
                title="Affordable"
                description="Voice calls keep costs low, so support is accessible to more families."
              />
              <VoiceBenefit
                icon={<Globe className="w-8 h-8 text-amber-600" />}
                iconBgColor="bg-amber-100"
                title="Accessible"
                description="Works with any phone — landline, flip phone, or mobile — anywhere, anytime."
              />
            </div>
          </div>
        </div>
      </section>

      {/* Trust Section */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-4xl mx-auto">
            <div className="space-y-6">
              <div className="space-y-4">
                <h2 className="text-4xl font-bold text-gray-900">
                  Built by Families, for Families
                </h2>
                <p className="text-xl text-gray-600 leading-relaxed">
                  Little Sister was founded by Wing Lam, a technology professional with over 10
                  years of experience who comes from an immigrant family background and lives across
                  the country from her aging parents.
                </p>
              </div>

              <div className="bg-gradient-to-r from-blue-50 to-sky-50 rounded-2xl p-8">
                <p className="text-lg text-gray-700 leading-relaxed italic mb-6">
                  "As my parents get older, I've watched technology expand into every aspect of
                  daily life - from needing an app just to pay bills to ordering food at
                  restaurants. Living across the country, I built Little Sister to bridge this
                  growing digital gap and help families stay connected."
                </p>
                <div className="text-center">
                  <p className="font-semibold text-gray-900">Wing Lam</p>
                  <p className="text-sm text-gray-600">Founder, Little Sister</p>
                </div>
              </div>

              <div className="grid md:grid-cols-3 gap-8 mt-12">
                <TrustPoint
                  icon={<Shield className="w-8 h-8 text-blue-600" />}
                  iconBgColor="bg-blue-100"
                  title="HIPAA Compliant"
                  description="Your family's privacy and medical information are protected with enterprise-grade security."
                />
                <TrustPoint
                  icon={<Users className="w-8 h-8 text-sky-600" />}
                  iconBgColor="bg-sky-100"
                  title="Family-First Design"
                  description="Every feature is designed with both aging adults and their adult children in mind."
                />
                <TrustPoint
                  icon={<Heart className="w-8 h-8 text-amber-600" />}
                  iconBgColor="bg-amber-100"
                  title="Personal Experience"
                  description="Built from real-world experience caring for aging parents from a distance."
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Pricing Section */}
      <section className="py-24 bg-gradient-to-r from-blue-50 to-sky-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="space-y-6">
            <span
              className="inline-block bg-blue-600 text-white px-6 py-3 rounded-full text-lg font-medium cursor-pointer hover:bg-blue-700 transition-colors"
              onClick={handleGetStarted}
            >
              Limited Time Beta Access
            </span>
            <h2 className="text-4xl font-bold text-gray-900">Free During Beta Phase</h2>
            <p className="text-xl text-gray-600 max-w-2xl mx-auto leading-relaxed">
              We're offering completely free access during our beta phase. Help us perfect Little
              Sister while providing invaluable support to your family.
            </p>

            <div className="bg-white rounded-2xl shadow-lg p-8 max-w-md mx-auto">
              <div className="space-y-4">
                <div className="text-center">
                  <div className="text-5xl font-bold text-blue-600">$0</div>
                  <div className="text-gray-600">During Beta Phase</div>
                  <div className="text-sm text-gray-500 mt-2">Future pricing: ~$29/month</div>
                </div>

                <div className="space-y-3 text-left">
                  <PricingFeature text="24/7 AI companionship" />
                  <PricingFeature text="Medication management" />
                  <PricingFeature text="Family dashboard & alerts" />
                  <PricingFeature text="Priority support" />
                </div>
              </div>
            </div>

            <p className="text-sm text-gray-600 max-w-xl mx-auto">
              Beta participants will receive advance notice and special pricing when we launch our
              subscription service. We believe in building a sustainable company that can serve
              families for years to come.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-slate-800">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="space-y-8">
            <div className="space-y-4">
              <h2 className="text-4xl font-bold text-white">Join Us as We Get Started</h2>
              <p className="text-xl text-gray-300 max-w-2xl mx-auto">
                We're just beginning our journey to help families stay connected. Sign up for
                notifications to hear the latest news from us.
              </p>
            </div>

            <form onSubmit={handleEmailSubmit} className="max-w-md mx-auto">
              <div className="flex gap-3">
                <input
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                  style={{
                    flex: 1,
                    minHeight: '44px',
                    fontSize: '16px',
                    padding: '12px 16px',
                    borderRadius: '8px',
                    border: '2px solid #e5e7eb',
                    backgroundColor: 'white',
                  }}
                />
                <Button type="submit" size="large">
                  Get Started
                  <ArrowRight style={{ width: '20px', height: '20px', marginLeft: '8px' }} />
                </Button>
              </div>
              <p className="text-sm text-gray-400 mt-3">
                No spam. Just updates on your Little Sister journey.
              </p>
            </form>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-8 pt-8 border-t border-slate-700">
              <div className="flex items-center gap-2 text-gray-400">
                <Mail style={{ width: '20px', height: '20px' }} />
                <span>hello@littlesister.ai</span>
              </div>
              <a
                href="/privacy"
                style={{ color: '#9ca3af', textDecoration: 'none', fontWeight: 500 }}
                onMouseEnter={e => (e.currentTarget.style.color = '#ffffff')}
                onMouseLeave={e => (e.currentTarget.style.color = '#9ca3af')}
              >
                Privacy Policy
              </a>
              <a
                href="/terms"
                style={{ color: '#9ca3af', textDecoration: 'none', fontWeight: 500 }}
                onMouseEnter={e => (e.currentTarget.style.color = '#ffffff')}
                onMouseLeave={e => (e.currentTarget.style.color = '#9ca3af')}
              >
                Terms & Conditions
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Password Gate Modal */}
      {showPasswordPrompt && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '1rem',
          }}
          onClick={() => setShowPasswordPrompt(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '2rem',
              maxWidth: '400px',
              width: '100%',
              boxShadow:
                '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <h2
              style={{
                fontSize: '1.5rem',
                fontWeight: 700,
                marginBottom: '0.5rem',
                color: '#111827',
              }}
            >
              Early Access
            </h2>
            <p style={{ fontSize: '0.875rem', color: '#6b7280', marginBottom: '1.5rem' }}>
              Enter the access code to continue
            </p>
            <input
              type="password"
              placeholder="Access code"
              value={password}
              onChange={e => {
                setPassword(e.target.value)
                setPasswordError(false)
              }}
              onKeyDown={e => {
                if (e.key === 'Enter') handlePasswordSubmit()
              }}
              autoFocus
              style={{
                width: '100%',
                padding: '12px 16px',
                borderRadius: '8px',
                border: `2px solid ${passwordError ? '#ef4444' : '#e5e7eb'}`,
                fontSize: '16px',
                marginBottom: '0.5rem',
                boxSizing: 'border-box',
              }}
            />
            {passwordError && (
              <p style={{ color: '#ef4444', fontSize: '0.875rem', marginBottom: '0.5rem' }}>
                Incorrect access code
              </p>
            )}
            <Button onClick={handlePasswordSubmit} style={{ width: '100%', marginTop: '0.5rem' }}>
              Continue
            </Button>
            <button
              onClick={() => setShowPasswordPrompt(false)}
              style={{
                marginTop: '1rem',
                width: '100%',
                padding: '0.75rem',
                border: 'none',
                backgroundColor: 'transparent',
                color: '#6b7280',
                fontSize: '0.875rem',
                cursor: 'pointer',
                borderRadius: '8px',
              }}
              onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f3f4f6')}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              Cancel
            </button>
          </div>
        </div>
      )}

      {/* Onboarding Choice Modal */}
      {showOnboardingChoice && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 9999,
            padding: '1rem',
          }}
          onClick={() => setShowOnboardingChoice(false)}
        >
          <div
            style={{
              backgroundColor: 'white',
              borderRadius: '16px',
              padding: '2rem',
              maxWidth: '600px',
              width: '100%',
              boxShadow:
                '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            }}
            onClick={e => e.stopPropagation()}
          >
            <h2
              style={{
                fontSize: '1.875rem',
                fontWeight: 700,
                marginBottom: '0.75rem',
                color: '#111827',
              }}
            >
              Choose Your Setup Method
            </h2>
            <p style={{ fontSize: '1rem', color: '#6b7280', marginBottom: '2rem' }}>
              Pick the method that works best for you
            </p>

            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
              {/* Voice Onboarding - Primary */}
              <button
                onClick={handleVoiceOnboarding}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '1rem',
                  padding: '1.5rem',
                  border: '2px solid #3b82f6',
                  borderRadius: '12px',
                  backgroundColor: '#eff6ff',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s',
                  width: '100%',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.transform = 'scale(1.02)'
                  e.currentTarget.style.boxShadow = '0 4px 6px -1px rgba(0, 0, 0, 0.1)'
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.transform = 'scale(1)'
                  e.currentTarget.style.boxShadow = 'none'
                }}
              >
                <div
                  style={{
                    width: '48px',
                    height: '48px',
                    backgroundColor: '#3b82f6',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Phone style={{ width: '24px', height: '24px', color: 'white' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '0.5rem',
                      marginBottom: '0.5rem',
                    }}
                  >
                    <h3
                      style={{ fontSize: '1.25rem', fontWeight: 600, color: '#111827', margin: 0 }}
                    >
                      Talk to Little Sister on the Phone
                    </h3>
                    <span
                      style={{
                        fontSize: '0.75rem',
                        fontWeight: 600,
                        color: '#3b82f6',
                        backgroundColor: 'white',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        border: '1px solid #3b82f6',
                      }}
                    >
                      Recommended
                    </span>
                  </div>
                  <p style={{ fontSize: '0.875rem', color: '#4b5563', margin: 0 }}>
                    Little Sister will call you and guide you through setup in a friendly
                    conversation. Takes about 5 minutes.
                  </p>
                </div>
              </button>

              {/* Form Onboarding - Secondary */}
              <button
                onClick={handleFormOnboarding}
                style={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: '1rem',
                  padding: '1.5rem',
                  border: '2px solid #e5e7eb',
                  borderRadius: '12px',
                  backgroundColor: 'white',
                  cursor: 'pointer',
                  textAlign: 'left',
                  transition: 'all 0.2s',
                  width: '100%',
                }}
                onMouseEnter={e => {
                  e.currentTarget.style.borderColor = '#d1d5db'
                  e.currentTarget.style.transform = 'scale(1.02)'
                }}
                onMouseLeave={e => {
                  e.currentTarget.style.borderColor = '#e5e7eb'
                  e.currentTarget.style.transform = 'scale(1)'
                }}
              >
                <div
                  style={{
                    width: '48px',
                    height: '48px',
                    backgroundColor: '#f3f4f6',
                    borderRadius: '12px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  <Mail style={{ width: '24px', height: '24px', color: '#6b7280' }} />
                </div>
                <div style={{ flex: 1 }}>
                  <h3
                    style={{
                      fontSize: '1.25rem',
                      fontWeight: 600,
                      color: '#111827',
                      marginBottom: '0.5rem',
                      margin: 0,
                    }}
                  >
                    Fill Out a Form
                  </h3>
                  <p style={{ fontSize: '0.875rem', color: '#6b7280', margin: 0 }}>
                    Prefer to type? Enter your information through our online form instead.
                  </p>
                </div>
              </button>
            </div>

            <button
              onClick={() => setShowOnboardingChoice(false)}
              style={{
                marginTop: '1.5rem',
                width: '100%',
                padding: '0.75rem',
                border: 'none',
                backgroundColor: 'transparent',
                color: '#6b7280',
                fontSize: '0.875rem',
                cursor: 'pointer',
                borderRadius: '8px',
                transition: 'background-color 0.2s',
              }}
              onMouseEnter={e => (e.currentTarget.style.backgroundColor = '#f3f4f6')}
              onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
            >
              Cancel
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

// Helper Components

interface FeatureCardProps {
  icon: React.ReactNode
  iconBgColor: string
  title: string
  description: string
  features: string[]
}

function FeatureCard({ icon, iconBgColor, title, description, features }: FeatureCardProps) {
  return (
    <div className="border-0 shadow-lg hover:shadow-xl transition-all duration-300 group bg-white rounded-xl p-6">
      <div
        className={`w-14 h-14 ${iconBgColor} rounded-2xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform`}
      >
        {icon}
      </div>
      <h3 className="text-xl font-semibold mb-3">{title}</h3>
      <p className="text-gray-600 leading-relaxed mb-4">{description}</p>
      <div className="space-y-2">
        {features.map((feature, idx) => (
          <div key={idx} className="flex items-center gap-2 text-sm text-gray-500">
            <CheckCircle className="w-4 h-4 text-blue-500" />
            {feature}
          </div>
        ))}
      </div>
    </div>
  )
}

interface OnboardingStepProps {
  number: string
  title: string
  description: string
}

function OnboardingStep({ number, title, description }: OnboardingStepProps) {
  return (
    <div className="text-center space-y-4">
      <div className="w-16 h-16 bg-blue-500 text-white rounded-full flex items-center justify-center mx-auto font-bold text-xl">
        {number}
      </div>
      <h3 className="text-xl font-semibold">{title}</h3>
      <p className="text-gray-600">{description}</p>
    </div>
  )
}

interface VoiceBenefitProps {
  icon: React.ReactNode
  iconBgColor: string
  title: string
  description: string
}

function VoiceBenefit({ icon, iconBgColor, title, description }: VoiceBenefitProps) {
  return (
    <div className="space-y-4">
      <div
        className={`w-16 h-16 ${iconBgColor} rounded-full mx-auto flex items-center justify-center`}
      >
        {icon}
      </div>
      <h4 className="text-xl font-semibold text-gray-900">{title}</h4>
      <p className="text-gray-600">{description}</p>
    </div>
  )
}

interface TrustPointProps {
  icon: React.ReactNode
  iconBgColor: string
  title: string
  description: string
}

function TrustPoint({ icon, iconBgColor, title, description }: TrustPointProps) {
  return (
    <div className="text-center space-y-3">
      <div
        className={`w-16 h-16 ${iconBgColor} rounded-full mx-auto flex items-center justify-center`}
      >
        {icon}
      </div>
      <h3 className="font-semibold text-gray-900">{title}</h3>
      <p className="text-gray-600 text-sm">{description}</p>
    </div>
  )
}

interface PricingFeatureProps {
  text: string
}

function PricingFeature({ text }: PricingFeatureProps) {
  return (
    <div className="flex items-center gap-3">
      <CheckCircle className="w-5 h-5 text-blue-500" />
      <span className="text-gray-700">{text}</span>
    </div>
  )
}
